import type { SessionInfo } from "@opencode/client/promise"
import { useQuery } from "@tanstack/solid-query"
import { createMemo, mapArray } from "solid-js"
import { loadHomeSessionIndex, mergeHomeSessionIndex } from "@/home/sessions/index"
import { homeProjectForSession } from "@/home/sessions/records"
import { ServerConnection } from "@/runtime/server/registry"
import { useGlobal, type ServerCtx } from "@/runtime/server/runtime"
import { displayName } from "@/shell/layout/helpers"
import { useSidebar } from "@/shell/sidebar/context"
import { sidebarKey, sortSidebarProjects } from "@/shell/sidebar/state"
import type { LocalProject } from "@/shell/state/layout"
import { pathKey } from "./path-key"

export type ProjectCatalogEntry = {
  key: string
  server: ServerConnection.Any
  project: LocalProject
  updated: number
}

// Share Home's metadata query without making its data a deeply reactive query result.
const selectSessions = (sessions: SessionInfo[]) => () => sessions

export function createProjectCatalog(options?: { projects?: (context: ServerCtx) => LocalProject[] }) {
  const global = useGlobal()
  const sidebar = useSidebar()
  const inventories = mapArray(
    () => global.servers.list().map(ServerConnection.key),
    (key) => {
      // Queries belong to a stable service identity, even when the connection list is reordered.
      const server = createMemo<ServerConnection.Any>(
        (previous) => global.servers.list().find((connection) => ServerConnection.key(connection) === key) ?? previous,
        global.servers.list().find((connection) => ServerConnection.key(connection) === key)!,
      )
      const context = () => global.ensureServerCtx(server())
      const projects = createMemo(() => options?.projects?.(context()) ?? context().projects.list())
      const sessions = useQuery(() => ({
        queryKey: ["home-sessions", server()] as const,
        enabled: projects().length > 0 && context().sdk.connection.status() === "connected",
        queryFn: ({ signal }) =>
          loadHomeSessionIndex((input, options) => context().sdk.api.session.list(input, options), signal),
        retry: false,
        staleTime: 30_000,
        refetchOnMount: true,
        refetchOnReconnect: true,
        select: selectSessions,
      }))
      const activity = createMemo(() => {
        const ctx = context()
        const updated = new Map<string, number>()
        const fetched = sessions.isPending ? [] : (sessions.data?.() ?? [])
        const merged = ctx.data.session.apply(mergeHomeSessionIndex(fetched, ctx.data.session.list()))
        for (const session of merged) {
          const directory = pathKey(homeProjectForSession(session, projects())?.worktree ?? session.location.directory)
          updated.set(directory, Math.max(updated.get(directory) ?? 0, session.time.updated ?? session.time.created))
        }
        return updated
      })
      const entry = (project: LocalProject): ProjectCatalogEntry => ({
        key: sidebarKey({ type: "project", server: key, directory: project.worktree }),
        server: server(),
        project,
        updated: Math.max(
          project.time?.updated ?? project.time?.created ?? 0,
          activity().get(pathKey(project.worktree)) ?? 0,
        ),
      })
      return {
        entries: createMemo(() => projects().map(entry)),
        // Recently removed directories allocate their own metadata stores. Only Home requests them.
        recentlyClosed: () => context().projects.recentlyClosed().map(entry),
      }
    },
  )
  const sort = (items: ProjectCatalogEntry[], order: readonly string[], legacy = sidebar.projectOrder) =>
    sortSidebarProjects(items, {
      key: (entry) => entry.key,
      server: (entry) => ServerConnection.key(entry.server),
      name: (entry) => displayName(entry.project),
      updated: (entry) => entry.updated,
      order,
      legacy,
    })
  const entries = createMemo(() =>
    sort(
      inventories().flatMap((inventory) => inventory.entries()),
      sidebar.projectListOrder(),
    ),
  )
  const recentlyClosed = () =>
    sort(
      inventories().flatMap((inventory) => inventory.recentlyClosed()),
      [],
      () => [],
    )
  return { entries, recentlyClosed }
}
